import { FC, Ref, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getAuthTeamMember,
  getChosenBusiness,
} from 'store/authentication/selectors';
import { PluginContext } from 'ts-components/Plugins/context';
import { INTERACTIVE_CAPABILITY } from 'ts-components/PiChat/protocol';
import { DEFAULT_KICODE_URL } from 'ts-components/PiChat/connectionDefaults';
import { useBuilderChat } from 'ts-components/PiChat/builder/useBuilderChat';
import { BuilderChatPanel } from 'ts-components/PiChat/builder/BuilderChatPanel';
import type { GuardBusy } from 'ts-components/PiChat/PiChatPanel';
import { ResumePendingPanel } from 'ts-components/KiCode/session/ResumePendingPanel';
import { useResumeConversationId } from 'ts-components/PiChat/kicodeSessions';
import { getActionDescriptions } from 'pages/AutomationEngine/hooks/useAddActionDescriptions';
import {
  getGroupedOptionsSelector,
  getProviderModelLookup,
} from 'pages/AutomationEngine/store/selectors';
import { StepIdLabel } from './StepIdLabel';
import { codeStepResumeQuery } from './codeStepResume';
import { shouldStartFresh, type ResumeAttempt } from './resumeFallback';
import {
  handleCodeStepSave,
  type AutomationStep,
  type AutomationStoreLike,
  type CodeStepAiContext,
  type SaveInFlight,
} from './codeStepSave';

const QA_PREFIX = 'code-step-ai-panel';
const selectGroupedOptions = getGroupedOptionsSelector();

export type CodeStepAiPanelProps = {
  context: CodeStepAiContext;
  store: AutomationStoreLike;
  resumeConversationId?: string | null;
  beforePersist: () => void;
  onClose: () => void;
  guardRef?: Ref<GuardBusy>;
  className?: string;
};

// The resume id resolves before the chat mounts so `session.start` carries it.
export const CodeStepAiPanel: FC<CodeStepAiPanelProps> = ({
  resumeConversationId = null,
  ...props
}) => {
  const employeeId: string | undefined = useSelector(getAuthTeamMember)?.id;
  const { automationId, stepId } = props.context;
  const resume = useResumeConversationId(
    codeStepResumeQuery(automationId, stepId, employeeId),
    resumeConversationId
  );
  // Starting the chat before the team member loads would remount it later.
  if (!employeeId || resume.pending) {
    return (
      <ResumePendingPanel
        qaPrefix={QA_PREFIX}
        onClose={props.onClose}
        className={props.className}
      />
    );
  }
  return (
    <CodeStepAiChat {...props} resumeConversationId={resume.conversationId} />
  );
};

const CodeStepAiChat: FC<CodeStepAiPanelProps> = ({
  context,
  store,
  resumeConversationId = null,
  beforePersist,
  onClose,
  guardRef,
  className,
}) => {
  const { t } = useTranslation();
  const business = useSelector(getChosenBusiness);
  const { automationSteps } = useContext(PluginContext);
  const conversationIdRef = useRef<string | null>(resumeConversationId);
  const attemptRef = useRef<ResumeAttempt>({
    sentConversationId: null,
    ready: false,
  });
  const freshTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [saveInFlight] = useState<SaveInFlight>(() => ({ active: false }));

  const describe = (step: AutomationStep): string => {
    const state = store.getState() as any;
    return getActionDescriptions({
      step,
      automation: state.automation,
      fields: state.fields,
      business,
      groupedOptions: selectGroupedOptions(state),
      metaData: state.metaData,
      automationSteps,
      providerModelLookup: getProviderModelLookup(state),
      t,
    });
  };

  const chat = useBuilderChat({
    filesWarning: 'Code step version files could not be loaded',
    onServerMessage: (message, meta) => {
      if (message.type === 'session.ready') {
        conversationIdRef.current = message.conversationId;
        attemptRef.current = { ...attemptRef.current, ready: true };
      }
      if (shouldStartFresh(attemptRef.current, message)) {
        conversationIdRef.current = null;
        clearTimeout(freshTimerRef.current);
        freshTimerRef.current = setTimeout(() => chat.connect());
      }
      if (message.type === 'code_step.save') {
        void handleCodeStepSave(
          message,
          meta,
          { store, context, describe, beforePersist },
          saveInFlight
        );
      }
    },
    getStart: () => {
      attemptRef.current = {
        sentConversationId: conversationIdRef.current,
        ready: false,
      };
      const conversationId = conversationIdRef.current;
      return {
        url: DEFAULT_KICODE_URL,
        profile: 'code-step-builder',
        capabilities: [INTERACTIVE_CAPABILITY],
        context: {
          targetType: 'workflow_step',
          automationId: context.automationId,
          stepId: context.stepId,
        },
        ...(conversationId ? { conversationId } : {}),
      };
    },
  });

  useEffect(() => () => clearTimeout(freshTimerRef.current), []);

  return (
    <BuilderChatPanel
      className={className}
      chat={chat}
      qaPrefix={QA_PREFIX}
      onClose={onClose}
      guardRef={guardRef}
      profile="code-step-builder"
      title={t('Run Code')}
      label={t('AI panel')}
      placeholder={t('Describe the code step')}
      emptyPrompt={t('Describe the code step you want to build')}
      aboveFileTree={<StepIdLabel stepId={context.stepId} />}
      fileTreeDefaultOpen={false}
    />
  );
};
